import { useCallback, useEffect, useRef, useState } from 'react'
import { useRoomContext } from '@livekit/components-react'
import { RemoteAudioTrack, RemoteTrack, RoomEvent, Track } from 'livekit-client'
import { useCallVolumeStore } from '../store/callVolumeStore'

export type AudioOutputMode = 'speaker' | 'earpiece'

export interface AudioOutputDevice {
  deviceId: string
  label: string
}

export interface UseAudioOutputControlResult {
  /** Whether setSinkId is available (desktop Chrome, Android Chrome). */
  supportsSinkId: boolean
  /** True on iOS where only the AudioContext toggle works. */
  isIOS: boolean
  /** Current mode — only meaningful on iOS (earpiece vs speaker). */
  mode: AudioOutputMode
  /** Toggle between speaker and earpiece — iOS only. */
  toggleMode: () => void
  /** Name of the connected Bluetooth/wired device (AirPods etc.) on iOS, or null if none. */
  connectedDeviceName: string | null
  /** Available audio output devices — populated when supportsSinkId is true. */
  outputDevices: AudioOutputDevice[]
  /** Currently selected output device ID. */
  selectedDeviceId: string
  /** Switch to a specific output device — desktop/Android only. */
  selectDevice: (deviceId: string) => Promise<void>
  /** Session output volume (0–100) for remote participants' voices (#1722).
   *  Read from `useCallVolumeStore`; applied audibly by `CallAudioRenderer`
   *  via `RemoteAudioTrack.setVolume()` (the webAudioMix gain path).
   *  Independent of the per-device selection above and of the local mic. */
  volume: number
  setVolume: (pct: number) => void
  /** Slider drag bookends — keep "speaker on" restoring the level a drag
   *  started from when the drag ends at 0. */
  beginVolumeAdjust: () => void
  endVolumeAdjust: () => void
  /** Speaker on/off is tied to the slider: off ⇒ 0%, on ⇒ last level > 0. */
  speakerOn: boolean
  toggleSpeakerOn: () => void
  /** iOS Speaker mode plays through plain <audio> elements, which iOS won't
   *  attenuate — only mute (0%) or full level take effect there. */
  volumeFixed: boolean
}

/**
 * Controls audio output routing for the active call.
 *
 * Desktop / Android Chrome: enumerates output devices and switches via
 * Room.switchActiveDevice (setSinkId under the hood).
 *
 * iOS (#1722 hybrid, by mode):
 *  - Earpiece (default): remote tracks stay on LiveKit's webAudioMix gain path,
 *    so the volume slider works.
 *  - Speaker: remote tracks move to plain HTML <audio> elements and a
 *    near-silent non-playsinline <audio> element forces iOS to route the
 *    page's audio session to the built-in loudspeaker. iOS ignores
 *    `el.volume`, so on this path only 0% (element muted) and full level
 *    take effect — surfaced as `volumeFixed`.
 *  Switching back to earpiece destroys the override element (iOS reverts to
 *  the default route — AirPods if connected, earpiece otherwise) and moves
 *  the tracks back onto the gain path, re-applying the level.
 */
export function useAudioOutputControl(
  audioContext: AudioContext | undefined
): UseAudioOutputControlResult {
  const room = useRoomContext()

  const [supportsSinkId] = useState(
    () =>
      typeof HTMLAudioElement !== 'undefined' &&
      'setSinkId' in HTMLAudioElement.prototype
  )
  const [isIOS] = useState(
    () =>
      /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      !(window as unknown as { MSStream?: unknown }).MSStream
  )

  // Start in earpiece mode (natural iOS routing: earphones if connected, earpiece if not).
  // Loudspeaker is the opt-in via the toggle — same convention as Zoom / FaceTime.
  const [mode, setMode] = useState<AudioOutputMode>('earpiece')

  // iOS speaker-override refs. Playing a near-silent audio element WITHOUT the
  // playsinline attribute forces iOS to route the entire page's audio session
  // through the built-in loudspeaker, overriding connected AirPods or wired
  // earphones. The oscillator node is kept at near-zero gain (not zero — iOS
  // stops routing if the stream is truly silent). Cleaned up on earpiece switch
  // or unmount.
  const speakerOscRef = useRef<OscillatorNode | null>(null)
  const speakerElRef = useRef<HTMLAudioElement | null>(null)
  const [connectedDeviceName, setConnectedDeviceName] = useState<string | null>(
    null
  )
  const [outputDevices, setOutputDevices] = useState<AudioOutputDevice[]>([])
  const [selectedDeviceId, setSelectedDeviceId] = useState('default')
  const volume = useCallVolumeStore((s) => s.volume)
  const setVolume = useCallVolumeStore((s) => s.setVolume)
  const beginVolumeAdjust = useCallVolumeStore((s) => s.beginAdjust)
  const endVolumeAdjust = useCallVolumeStore((s) => s.endAdjust)
  const toggleSpeakerOn = useCallVolumeStore((s) => s.toggleSpeakerOn)
  const volumeRef = useRef(volume)
  volumeRef.current = volume

  // Set further down once the track helpers exist; read from the devicechange
  // handler below, which is registered before them.
  const returnToGainPathRef = useRef<() => void>(() => {})

  const destroySpeakerOverride = useCallback(() => {
    speakerOscRef.current?.stop()
    speakerOscRef.current = null
    if (speakerElRef.current) {
      speakerElRef.current.pause()
      speakerElRef.current.srcObject = null
      speakerElRef.current.remove()
      speakerElRef.current = null
    }
  }, [])

  // On iOS, detect connected headphone/AirPods name via enumerateDevices audioinput.
  // Mic permission is already granted during the call so labels are populated.
  // When AirPods connect mid-call: exit speaker override (if active) so iOS naturally
  // routes to the headphones, then force-replay all audio elements on the new route.
  useEffect(() => {
    if (!isIOS) return

    const detect = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const external = devices.find(
          (d) =>
            d.kind === 'audioinput' &&
            d.label &&
            !d.label.toLowerCase().includes('built-in') &&
            !d.label.toLowerCase().includes('default')
        )
        const name = external?.label
          ? external.label.replace(/\s*microphone$/i, '').trim() || null
          : null
        setConnectedDeviceName(name)

        if (name) {
          // AirPods/headphones just connected — destroy the speaker override so iOS
          // routes to the external device instead of the built-in speaker, and
          // return the tracks to the gain path that Earpiece mode uses.
          destroySpeakerOverride()
          returnToGainPathRef.current()
          setMode('earpiece')
          // Force-replay all audio elements so iOS picks up the new route.
          document.querySelectorAll('audio').forEach((el) => {
            if (el.srcObject !== null) {
              void el.play().catch(() => {})
            }
          })
        }
      } catch {
        setConnectedDeviceName(null)
      }
    }

    void detect()
    navigator.mediaDevices.addEventListener('devicechange', detect)
    return () =>
      navigator.mediaDevices.removeEventListener('devicechange', detect)
  }, [isIOS, destroySpeakerOverride])

  // Enumerate audio output devices whenever setSinkId is available.
  useEffect(() => {
    if (!supportsSinkId) return

    const enumerate = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const outputs = devices
          .filter((d) => d.kind === 'audiooutput')
          .map((d, i) => ({
            deviceId: d.deviceId,
            label: d.label || `Speaker ${i + 1}`,
          }))
        setOutputDevices(outputs)
      } catch {
        // Permission denied or API unavailable — leave list empty.
      }
    }

    enumerate()
    navigator.mediaDevices.addEventListener('devicechange', enumerate)
    return () =>
      navigator.mediaDevices.removeEventListener('devicechange', enumerate)
  }, [supportsSinkId])

  // ── iOS helpers ────────────────────────────────────────────────────────────

  // The context LiveKit mixes into. `audioContext` is the one created in the
  // Join gesture; after a reload-resume it is undefined and LiveKit created its
  // own, which we can only reach through the room's (private) field.
  const getMixContext = useCallback(
    (): AudioContext | undefined =>
      audioContext ??
      (room as unknown as { audioContext?: AudioContext }).audioContext,
    [room, audioContext]
  )

  const forEachRemoteAudioTrack = useCallback(
    (fn: (track: RemoteAudioTrack, source: Track.Source) => void) => {
      for (const [, participant] of room.remoteParticipants) {
        for (const [, publication] of participant.trackPublications) {
          if (publication.track instanceof RemoteAudioTrack) {
            fn(publication.track, publication.source)
          }
        }
      }
    },
    [room]
  )

  // HTML path (iOS Speaker mode). setAudioContext is @internal — ignore if it
  // fails on a future version.
  const moveToHtmlAudio = useCallback(
    (track: RemoteAudioTrack, source: Track.Source) => {
      try {
        track.setAudioContext(undefined)
      } catch {
        return
      }
      const level = volumeRef.current / 100
      track.setVolume(level)
      for (const el of track.attachedElements) {
        // Only the mic is tied to the slider; iOS honours `muted`, not `volume`.
        el.muted = source === Track.Source.Microphone && level === 0
        // The element may be left paused; force-play while still inside the
        // user gesture context.
        if (el.paused && el.srcObject !== null) void el.play().catch(() => {})
      }
    },
    []
  )

  // Gain path (iOS Earpiece mode, and everywhere else).
  const moveToGainPath = useCallback(
    (track: RemoteAudioTrack, source: Track.Source) => {
      const ctx = getMixContext()
      if (!ctx) return
      try {
        // iOS may suspend the AudioContext when the page was backgrounded.
        if (ctx.state === 'suspended') void ctx.resume()
        track.setAudioContext(ctx)
      } catch {
        return
      }
      // LiveKit silences the element only on first attach; after a round trip
      // through the HTML path we must silence it again or it double-plays.
      for (const el of track.attachedElements) {
        el.volume = 0
        el.muted = true
      }
      track.setVolume(
        source === Track.Source.Microphone ? volumeRef.current / 100 : 1
      )
    },
    [getMixContext]
  )

  const modeRef = useRef(mode)
  modeRef.current = mode
  returnToGainPathRef.current = () => {
    if (modeRef.current === 'speaker') forEachRemoteAudioTrack(moveToGainPath)
  }

  // A track subscribed while in Speaker mode joins the HTML path before it is
  // attached, so LiveKit never wires it into the gain path at all.
  useEffect(() => {
    if (!isIOS) return

    const handleSubscribed = (track: RemoteTrack) => {
      if (!(track instanceof RemoteAudioTrack) || modeRef.current !== 'speaker')
        return
      const pub = Array.from(room.remoteParticipants.values())
        .flatMap((p) => Array.from(p.trackPublications.values()))
        .find((p) => p.track === track)
      const source = pub?.source ?? Track.Source.Unknown
      moveToHtmlAudio(track, source)
      // The element attaches a tick after this event (React renders it) —
      // re-apply the mute-at-0 and force-play once it exists.
      window.setTimeout(() => moveToHtmlAudio(track, source), 50)
    }

    room.on(RoomEvent.TrackSubscribed, handleSubscribed)
    return () => {
      room.off(RoomEvent.TrackSubscribed, handleSubscribed)
    }
  }, [room, isIOS, moveToHtmlAudio])

  // HTML path at 0%: the mic elements must be muted (iOS ignores el.volume).
  useEffect(() => {
    if (!isIOS || mode !== 'speaker') return
    forEachRemoteAudioTrack((track, source) => {
      if (source !== Track.Source.Microphone) return
      for (const el of track.attachedElements) el.muted = volume === 0
    })
  }, [isIOS, mode, volume, forEachRemoteAudioTrack])

  const toggleMode = useCallback(() => {
    const next: AudioOutputMode = mode === 'speaker' ? 'earpiece' : 'speaker'
    setMode(next)

    if (next === 'speaker' && audioContext) {
      // Move remote tracks onto HTML <audio> elements so they sit on the same
      // audio session as the non-playsinline element below. Routing through
      // AudioContext would bypass the session override and keep audio on
      // AirPods even after the trick fires.
      forEachRemoteAudioTrack(moveToHtmlAudio)

      // A non-playsinline <audio> element with any audio forces iOS to switch
      // the page's audio session to the built-in loudspeaker.
      try {
        destroySpeakerOverride()
        const dest = audioContext.createMediaStreamDestination()
        const osc = audioContext.createOscillator()
        const gain = audioContext.createGain()
        gain.gain.value = 0.001 // near-silent but non-zero — iOS ignores truly silent streams
        osc.connect(gain)
        gain.connect(dest)
        osc.start()
        speakerOscRef.current = osc

        const el = document.createElement('audio')
        el.srcObject = dest.stream
        // NO playsinline — this is what triggers the speaker session override.
        document.body.appendChild(el)
        void el.play().catch(() => {})
        speakerElRef.current = el

        // Force-resume any paused LiveKit audio elements under the new session.
        document.querySelectorAll('audio').forEach((a) => {
          if (a !== el && a.paused && a.srcObject !== null)
            void a.play().catch(() => {})
        })
      } catch {
        // AudioContext unavailable — fall back silently.
      }
    } else {
      // Earpiece: destroy override so iOS reverts to the default route
      // (AirPods / earpiece), and put the tracks back on the gain path so the
      // slider works again at the current level.
      destroySpeakerOverride()
      forEachRemoteAudioTrack(moveToGainPath)
    }
  }, [
    mode,
    audioContext,
    destroySpeakerOverride,
    forEachRemoteAudioTrack,
    moveToHtmlAudio,
    moveToGainPath,
  ])

  // Unmount (call ended, or InCallView swapped for the floating widget): drop
  // the loudspeaker override and, if we were in Speaker mode, return the
  // tracks to the gain path — the next mount starts in Earpiece mode, and the
  // widget's slider must stay audible.
  const unmountRef = useRef<() => void>(() => {})
  unmountRef.current = () => {
    destroySpeakerOverride()
    if (isIOS) returnToGainPathRef.current()
  }
  useEffect(() => () => unmountRef.current(), [])

  // ── Desktop / Android helpers ──────────────────────────────────────────────

  const selectDevice = useCallback(
    async (deviceId: string) => {
      setSelectedDeviceId(deviceId)
      try {
        await room.switchActiveDevice('audiooutput', deviceId)
      } catch (err) {
        console.error('switchActiveDevice failed:', err)
      }
    },
    [room]
  )

  return {
    supportsSinkId,
    isIOS,
    mode,
    toggleMode,
    connectedDeviceName,
    outputDevices,
    selectedDeviceId,
    selectDevice,
    volume,
    setVolume,
    beginVolumeAdjust,
    endVolumeAdjust,
    speakerOn: volume > 0,
    toggleSpeakerOn,
    volumeFixed: isIOS && mode === 'speaker',
  }
}
